import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAdQuery, useGetAdChartsQuery, useToggleAdStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import BarChart from '../../../components/charts/BarChart';
import AreaChart from '../../../components/charts/AreaChart';
import PieChart from '../../../components/charts/PieChart';
import env from '../../../config/env';
import { getStatusColor, formatCurrency, toExternalUrl } from '../../../utils/helpers';
import Swal from 'sweetalert2';
import { useLanguage } from '../../../context/LanguageContext';

// Format date as "d M, Y" (e.g., "27 Mar, 2023")
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminAdDetails: React.FC = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useGetAdQuery(id!);
  const { data: chartsData } = useGetAdChartsQuery(id!);
  const [toggleStatus, { isLoading: isTogglingStatus }] = useToggleAdStatusMutation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusButtonClass = (status: string | null | undefined): string => {
    const currentStatus = status || 'Pending';
    switch (currentStatus) {
      case 'Active':
        return 'btn btn-success dropdown-toggle';
      case 'Rejected':
        return 'btn btn-danger dropdown-toggle';
      case 'Pending':
      default:
        return 'btn btn-warning dropdown-toggle';
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsDropdownOpen(false);

    let rejectionReason = '';

    if (newStatus === 'Rejected') {
      const { value, isConfirmed } = await Swal.fire({
        title: t('Reject Ad'),
        input: 'textarea',
        inputLabel: t('Rejection Reason'),
        inputPlaceholder: t('Enter rejection reason (optional)…'),
        inputAttributes: { rows: '3' },
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: t('Reject Ad'),
      });
      if (!isConfirmed) return;
      rejectionReason = value || '';
    } else {
      const result = await Swal.fire({
        title: t('Are you sure?'),
        text: `${t('Do you want to change the status to')} ${newStatus}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('Yes, update it!'),
      });
      if (!result.isConfirmed) return;
    }

    try {
      const payload: any = { id, status: newStatus };
      if (rejectionReason) payload.rejection_reason = rejectionReason;

      Swal.fire({
        title: newStatus === 'Rejected' ? t('Processing Rejection…') : t('Updating Status…'),
        html: newStatus === 'Rejected'
          ? t('Sending notification and processing refund. Please wait…')
          : t('Please wait…'),
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      await toggleStatus(payload).unwrap();

      Swal.fire({
        icon: 'success',
        title: t('Updated!'),
        text: `${t('Ad status has been updated to')} ${newStatus}.`,
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire(t('Error!'), err?.data?.message || t('Failed to update ad status.'), 'error');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="d-flex flex-column flex-column-fluid">
          <div className="alert alert-danger m-5">
            {error ? t('Error loading ad details') : t('Ad not found')}
          </div>
        </div>
      </div>
    );
  }

  const ad = data.data;
  const analytics = ad.analytics;
  const imageBaseUrl = env.apiUrl.replace('/api', '');
  const adPerformance = analytics?.ad_performance;
  const engagementMetrics = analytics?.engagement_metrics;
  const conversionAndContact = analytics?.conversion_and_contact;
  const financialMetrics = analytics?.financial_metrics;
  const audienceAndReach = analytics?.audience_and_reach;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Ad Details')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ads'); }} className="text-muted text-hover-primary">{t('Ads')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{t('Details')}</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
            {ad.deleted_at ? (
                <span className="badge bg-danger  p-3">
                  {t('Deleted')}
                </span>
            ):(
              <>
              {ad.status === 'Draft' ? (
                <span className="badge badge-light-secondary fs-7 fw-bold px-4 py-3">
                  <i className="bi bi-lock-fill me-2 text-muted"></i>{t('Draft — Status locked')}
                </span>
              ) : (
              <div className="dropdown" ref={dropdownRef}>
                <button
                  className={getStatusButtonClass(ad.status)}
                  type="button"
                  onClick={() => !isTogglingStatus && setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                  disabled={isTogglingStatus}
                >
                  {isTogglingStatus ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('Processing…')}
                    </>
                  ) : (
                    t(ad.status || 'Pending')
                  )}
                </button>
                <ul
                  className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
                  style={isDropdownOpen ? { display: 'block' } : {}}
                >
                  {/* <li>
                    <button
                      type="button"
                      className="dropdown-item d-flex align-items-center"
                      onClick={() => handleStatusChange('Pending')}
                    >
                      <span className="bullet bullet-dot bg-warning me-2 h-10px w-10px"></span>
                      Pending
                    </button>
                  </li> */}
                  {['Pending', 'Paused'].includes(ad.status) &&
                    <li>
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => handleStatusChange('Active')}
                      >
                        <span className="bullet bullet-dot bg-success me-2 h-10px w-10px"></span>
                        {t('Approved')}
                      </button>
                    </li>
                  }
                  {['Pending'].includes(ad.status) &&
                    <li>
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => handleStatusChange('Rejected')}
                      >
                        <span className="bullet bullet-dot bg-danger me-2 h-10px w-10px"></span>
                        {t('Rejected')}
                      </button>
                    </li>
                  }
                  {['Active'].includes(ad.status) &&
                    <li>
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => handleStatusChange('Paused')}
                      >
                        <span className="bullet bullet-dot bg-primary me-2 h-10px w-10px"></span>
                        {t('Paused')}
                      </button>
                    </li>
                  }


                </ul>
              </div>
              )}
              </>
            )}
              
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => navigate(-1)}
              >
                <i className={`fas ${lang === 'ar' ? 'fa-arrow-right' : 'fa-arrow-left'} me-2`}></i>
                {t('Back to List')}
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="row g-5 g-xl-10">
              {/* Main Content */}
              <div className="col-xl-8">
                {/* Basic Information Card */}
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold fs-3 mb-1">{t('Basic Information')}</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Title')}</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{ad.title || 'N/A'}</span>
                      </div>
                    </div>
                    {ad.description && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Description')}</label>
                        <div className="col-lg-8">
                          <span className="fw-normal fs-6 text-gray-600">{ad.description}</span>
                        </div>
                      </div>
                    )}
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Category')}</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-primary fs-7">
                          {(lang === 'ar' ? ad.ad_category?.title_ar : ad.ad_category?.title) || ad.ad_category?.title || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {ad.sub_category && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Sub Category')} </label>
                        <div className="col-lg-8">
                          <span className="badge badge-light-success fs-7">
                            {(lang === 'ar' ? ad.sub_category?.title_ar : ad.sub_category?.title) || ad.sub_category?.title || 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                    {ad.category_type && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted"> {t('Category Type')} </label>
                        <div className="col-lg-8">
                          <span className="badge badge-light-success fs-7">
                            {(lang === 'ar' ? ad.category_type?.title_ar : ad.category_type?.title) || ad.category_type?.title || 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Type')}</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-info fs-7">
                          {(lang === 'ar' ? ad.ad_type?.title_ar : ad.ad_type?.title) || ad.ad_type?.title || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {ad.strategic_ad_type && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Strategic Type')}</label>
                        <div className="col-lg-8">
                          <span className="badge badge-light-info fs-7">
                            {(lang === 'ar' ? ad.strategic_ad_type?.title_ar : ad.strategic_ad_type?.title) || ad.strategic_ad_type?.title || 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                    {ad?.ad_destination && (

                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Ad Destination')} </label>
                      <div className="col-lg-8">
                        <span className=" text-uppercase fs-7">
                          {ad.ad_destination || 'N/A'}
                        </span>
                      </div>
                    </div>
                    )}

                    {ad?.meta_data && ad.meta_data.cta_button_text && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          {t('CTA Button Text')}
                        </label>
                        <div className="col-lg-8">
                              <span className="fw-bold badge bg-light-primary text-dark">{ad.meta_data.cta_button_text || 'N/A'}</span>
                        </div>
                       
                      </div>
                    )}
                    {ad?.meta_data && ad.meta_data.external_link && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          {t('External Link')}
                        </label>
                        <div className="col-lg-8">
                            <a href={toExternalUrl(ad.meta_data.external_link) ?? undefined} target="_blank" rel="noopener noreferrer">
                              <span className=" text-info">{ad.meta_data.external_link || t('N/A')}</span>
                            </a>
                        </div>
                      </div>
                    )}

                    {ad?.meta_data && ad.meta_data.target_ad && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          {t('Target Ad')}
                        </label>
                        <div className="col-lg-8">
                              <span className="fw-bold badge bg-light-primary text-dark">#{ad.meta_data.target_ad.ad_id || 'N/A'}</span>
                              <p className="fw-bold text-gray-800">{ad.meta_data.target_ad.title || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                    {ad?.meta_data && ad.meta_data.notification_title && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          {t('Notification Title')}
                        </label>
                        <div className="col-lg-8">
                              <p className="mb-1 fw-bold text-dark">{ad.meta_data.notification_title || 'N/A'}</p>
                              <p className=" text-muted">{ad.meta_data.notification_description || 'N/A'}</p>
                        </div>
                      </div>
                    )}

                    {ad.ad_package && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Package')}</label>
                        <div className="col-lg-8">
                            <span className="badge badge-light-warning fs-6 mb-1">
                              {(lang === 'ar' ? ad.ad_package.title_ar : ad.ad_package.title) || ad.ad_package.title || t('N/A')}
                            </span> <br />
                            <p className="fw-bold mb-1 text-gray-700">{t('Payment Method:')} {ad.payment_method || t('N/A')}</p>
                            <p className="fw-bold mb-1 text-gray-700">{t('Transaction ID:')} {ad.transaction_id || t('N/A')}</p>
                            <p className="fw-bold mb-1 text-gray-900">{t('Pay Amount:')} {formatCurrency(ad.payment_amount)}</p>
                        </div>
                      </div>
                    )}
                   
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Language')}</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-success fs-7">
                          {ad.ad_language?.toUpperCase() || 'EN'}
                        </span>
                      </div>
                    </div>
                    {ad.ad_language ==='both' &&
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Same Content')}</label>
                        <div className="col-lg-8">
                          <span className={`badge ${ad.same_content ? 'badge-light-success' : 'badge-light-danger'} fs-7`}>
                            {ad.same_content ? t('Yes') : t('No')}
                          </span>
                        </div>
                      </div>
                    }
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Status')}</label>
                      <div className="col-lg-8">
                        <span className={`badge ${getStatusColor(ad.status)} fs-7`}>
                          {ad.status ? t(ad.status) : t('N/A')}
                        </span>
                      </div>
                    </div>
                    {ad.status === 'Rejected' && ad.rejection_reason && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">{t('Rejection Reason')}</label>
                        <div className="col-lg-8">
                          <span className="fw-normal fs-6 text-danger">{ad.rejection_reason}</span>
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <label className="col-lg-4 fw-semibold text-muted">{t('Created Date')}</label>
                      <div className="col-lg-8">
                        <span className="fw-normal fs-6 text-gray-600">{formatDate(ad.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images Card */}
                {ad.images && Array.isArray(ad.images) && ad.images.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">{t('Images')}</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.images.length} {t('image(s)')}</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="row g-3">
                        {ad.images.map((image: string, index: number) => {
                          return (
                            <div key={index} className="col-md-4">
                              <div className="card-rounded overflow-hidden border border-gray-200">
                                <Image 
                                  src={image} 
                                  alt={`Ad image ${index + 1}`}
                                  className="w-100 h-200px object-fit-cover"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Fields Card */}
                {ad.custom_fields && ad.custom_fields.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">{t('Ad Details')}</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.custom_fields.length} {t('field(s)')}</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="fw-bold text-muted">
                              <th className="min-w-150px"> {t('Name')}</th>
                              <th className="min-w-200px">{t('Value')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ad.custom_fields.map((field: any) => {
                              // Handle both pivot.value and direct value access
                              const fieldValue = field.pivot?.value || field.value || 'N/A';
                              return (
                                <tr key={field.id}>
                                  <td>
                                    <span className="text-gray-800 fw-bold">{(lang === 'ar' ? field.field_name_ar : field.field_name) || field.field_name || t('N/A')}</span>
                                  </td>
                                 
                                  <td>
                                    {(() => {
                                      if (field.field_type === 'file_multiple' && Array.isArray(fieldValue)) {
                                        return (
                                          <div className="row g-3">
                                            {fieldValue.map((file: string, index: number) => {
                                              return (
                                                <div key={index} className="col-md-4">
                                                  <div className="card-rounded overflow-hidden border border-gray-200">
                                                    <Image 
                                                      src={file} 
                                                      alt={`Ad image ${index + 1}`}
                                                      className="w-100 h-200px object-fit-cover"
                                                    />
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      } else if (field.field_type === 'file_single') {
                                        return (
                                          <div className="card-rounded overflow-hidden border border-gray-200" style={{ maxWidth: '200px' }}>
                                            <Image 
                                              src={fieldValue} 
                                              alt={String(fieldValue)}
                                              className="w-100 object-fit-cover"
                                              style={{ height: '150px' }}
                                            />
                                          </div>
                                        );
                                      }
                                      else if (field.field_type === 'dropdown_multiple') {
                                        return (
                                          <>
                                            {fieldValue?.split(',')?.map((value: string, index: number) => (
                                              <span key={index} className="badge bg-secondary text-dark m-1" >
                                                {value}
                                              </span>
                                            ))}
                                          </>
                                        );
                                      }
                                      else if (fieldValue === field.field_key+'_checkbox') {
                                        return (
                                              <span  className="" >
                                                {field.checkbox_title}
                                              </span>
                                        );
                                      } else {
                                        return <span className="text-gray-800">{String(fieldValue)}</span>;
                                      }
                                    })()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="col-xl-4">
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold fs-3 mb-1">{t('Analytics')}</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    {analytics ? (
                      <>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">{t('Ad Performance')}</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Total Views')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.total_views ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Reach')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.reach ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Total Clicks')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.total_clicks ?? '-'}
                              </span>
                            </div>
                            {/* Outbound Clicks - Only for Strategic Ads */}
                            {ad.strategic_ad_type && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">{t('Outbound Clicks')}</span>
                                <span className="fw-bold text-gray-800">
                                  {adPerformance?.outbound_clicks ?? '-'}
                                </span>
                              </div>
                            )}
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Ad Duration')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.ad_duration ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Remaining Duration')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.remaining_duration ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Bounce Rate')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.bounce_rate ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Repeat Visitors')}</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.repeat_visitors ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">{t('Engagement Metrics')}</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Favorites')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.favorites ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Shares')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.shares ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Comments')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.comments ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Engagement Score')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.engagement_score ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Scroll Depth')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.scroll_depth ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Average Time on Ad')}</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.average_time_on ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">{t('Conversion & Contact')}</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Messages')}</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.messages ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Cost Per Message')}</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.cost_per_message ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Conversion Rate')}</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.conversion_rate ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">{t('Financial Metrics')}</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Total Spending')}</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.total_spending ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Average CPC')}</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.average_cpc ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Average CPM')}</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.average_cpm ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Cost Per Contact')}</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.cost_per_contact ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">{t('Click Through Rate')}</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.click_through_rate ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="fw-bold text-gray-800 mb-3">{t('Audience and Reach')}</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="row mb-2">
                              <span className="col-5 text-muted">{t('Top Locations')}</span>
                              <span className="col-7 fw-bold text-gray-800 text-end">
                                {audienceAndReach?.top_locations ?? '-'}
                              </span>
                            </div>
                            <div className="row">
                              <span className="col-5 text-muted">{t('Traffic Sources')}</span>
                              <span className="col-7 fw-bold text-gray-800 text-end">
                                {audienceAndReach?.traffic_sources ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">{t('No analytics data available.')}</div>
                    )}
                  </div>
                </div>

                {/* Advertiser Details Card */}
                {ad.user && (
                  <div
                    className="card mb-5 mb-xl-10 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/admin/users/${ad.user.id}`)}
                    role="button"
                    title={t('View advertiser profile')}
                    style={{ transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 0.5rem 1.5rem rgba(57, 106, 84, 0.18)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                  >
                    {/* Gradient header with avatar */}
                    <div
                      className="d-flex align-items-center px-6 py-5"
                      style={{ background: 'linear-gradient(135deg, #396A54 0%, #2c5240 100%)' }}
                    >
                      <div className="symbol symbol-60px me-4">
                        <span className="symbol-label fs-2 fw-bold text-white" style={{ background: 'rgba(255,255,255,0.18)' }}>
                          {(ad.user?.name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <div className="text-white fw-bold fs-4 mb-1">{ad.user?.name || 'N/A'}</div>
                        <span className="badge fs-8 fw-bold text-capitalize" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                          {ad.user?.user_type ? t(ad.user.user_type.replace(/_/g, ' ')) : t('Advertiser')}
                        </span>
                      </div>
                      <i className="bi bi-chevron-right fs-3 text-white opacity-75"></i>
                    </div>

                    <div className="card-body pt-5">
                      <div className="text-muted fw-semibold fs-7 text-uppercase mb-4">{t('Advertiser Details')}</div>

                      {ad.user?.email && (
                        <div className="d-flex align-items-center mb-5">
                          <span className="symbol symbol-35px me-3">
                            <span className="symbol-label bg-light-primary">
                              <i className="fas fa-envelope fs-5 text-primary"></i>
                            </span>
                          </span>
                          <div className="d-flex flex-column">
                            <span className="text-muted fs-8">{t('Email')}</span>
                            <span className="fw-semibold fs-6 text-gray-800">{ad.user.email}</span>
                          </div>
                        </div>
                      )}

                      {ad.user?.phone && (
                        <div className="d-flex align-items-center mb-5">
                          <span className="symbol symbol-35px me-3">
                            <span className="symbol-label bg-light-success">
                              <i className="fas fa-phone-alt fs-5 text-success"></i>
                            </span>
                          </span>
                          <div className="d-flex flex-column">
                            <span className="text-muted fs-8">{t('Phone')}</span>
                            <span className="fw-semibold fs-6 text-gray-800">{ad.user.phone}</span>
                          </div>
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-between pt-3 border-top border-gray-200">
                        <span className="text-muted fw-semibold fs-7">{t('Verification')}</span>
                        <span className={`badge fs-7 fw-bold ${
                          ad.user?.verification_badge?.includes('Green') ? 'badge-light-success' :
                          ad.user?.verification_badge?.includes('Gold') ? 'badge-light-warning' :
                          ad.user?.verification_badge?.startsWith('Requested') ? 'badge-light-warning' :
                          ad.user?.verification_badge?.startsWith('Rejected') ? 'badge-light-danger' :
                          'badge-light-secondary text-dark'
                        }`}>
                          {ad.user?.verification_badge ? t(ad.user.verification_badge) : t('Unverified')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Countries Card */}
                {ad.countries && ad.countries.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">{t('Countries')}</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.countries.length} {t('country/countries')}</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="d-flex flex-wrap gap-2">
                        {ad.countries.map((country: any) => (
                          <span key={country.id} className="badge badge-light-primary fs-7">
                            {(lang === 'ar' ? country.name_ar : country.name) || country.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cities Card */}
                {ad.cities && ad.cities.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">{t('Cities')}</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.cities.length} {t('city/cities')}</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="d-flex flex-wrap gap-2">
                        {ad.cities.map((city: any) => (
                          <span key={city.id} className="badge badge-light-info fs-7">
                            {(lang === 'ar' ? city.name_ar : city.name) || city.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}


              </div>
            </div>

            {/* Analytics Charts Row */}
            {analytics && (
              <div className="row g-5 g-xl-10 mt-2">

                {/* Performance Bar Chart */}
                <div className="col-12 col-xl-8">
                  <div className="card card-flush">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">{t('Performance Overview')}</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Views, clicks, reach, shares & engagement')}</span>
                      </h3>
                    </div>
                    <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                      <BarChart
                        labels={[t('Views'), t('Clicks'), t('Reach'), t('Shares'), t('Comments'), t('Favorites')]}
                        datasets={[{
                          label: t('Count'),
                          data: [
                            ad.ad_stat?.views ?? 0,
                            ad.ad_stat?.clicks ?? 0,
                            ad.ad_stat?.reach ?? 0,
                            ad.ad_stat?.shares ?? 0,
                            ad.ad_stat?.comments ?? 0,
                            parseInt(engagementMetrics?.favorites ?? '0'),
                          ],
                          backgroundColor: 'rgba(57, 106, 84, 0.75)',
                          borderColor: 'rgba(57, 106, 84, 1)',
                          barThickness: 36,
                        }]}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                            x: { grid: { display: false } },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Engagement Doughnut */}
                <div className="col-12 col-xl-4">
                  <div className="card card-flush h-100">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">{t('Engagement Breakdown')}</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Shares, comments & favorites')}</span>
                      </h3>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center pt-0 pb-5" style={{ minHeight: 240 }}>
                      {(ad.ad_stat?.shares || ad.ad_stat?.comments || engagementMetrics?.favorites) ? (
                        <div style={{ width: '100%', maxWidth: 280, height: 220 }}>
                          <PieChart
                            labels={[t('Shares'), t('Comments'), t('Favorites')]}
                            data={[
                              ad.ad_stat?.shares ?? 0,
                              ad.ad_stat?.comments ?? 0,
                              parseInt(engagementMetrics?.favorites ?? '0'),
                            ]}
                            backgroundColor={[
                              'rgba(57, 106, 84, 0.85)',
                              'rgba(57, 106, 84, 0.50)',
                              'rgba(33, 37, 41, 0.65)',
                            ]}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  display: true,
                                  position: 'bottom' as const,
                                  labels: { boxWidth: 12, padding: 12, font: { size: 11 }, usePointStyle: true },
                                },
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-muted">{t('No engagement data yet')}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Views & Clicks Timeline Chart */}
                {chartsData?.data?.timeline?.labels?.length > 0 && (
                  <div className="col-12">
                    <div className="card card-flush">
                      <div className="card-header pt-5">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark">{t('Views & Clicks Over Time')}</span>
                          <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Daily/weekly activity timeline')}</span>
                        </h3>
                      </div>
                      <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                        <AreaChart
                          labels={chartsData.data.timeline.labels}
                          datasets={[
                            {
                              label: t('Views'),
                              data: chartsData.data.timeline.views,
                              backgroundColor: 'rgba(57, 106, 84, 0.18)',
                              borderColor: 'rgba(57, 106, 84, 1)',
                            },
                            {
                              label: t('Clicks'),
                              data: chartsData.data.timeline.clicks,
                              backgroundColor: 'rgba(100, 116, 139, 0.18)',
                              borderColor: 'rgba(100, 116, 139, 0.85)',
                            },
                          ]}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: true, position: 'top' as const },
                            },
                            scales: {
                              y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                              x: { grid: { display: false } },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Views & Clicks by Country */}
                {(chartsData?.data?.by_country?.views?.length > 0 || chartsData?.data?.by_country?.clicks?.length > 0) && (
                  <div className="col-12">
                    <div className="card card-flush">
                      <div className="card-header pt-5">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark">{t('Audience by Country')}</span>
                          <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('Views and clicks per user country')}</span>
                        </h3>
                      </div>
                      <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                        {(() => {
                          const viewsArr: { country: string; total: number }[] = chartsData.data.by_country.views || [];
                          const clicksArr: { country: string; total: number }[] = chartsData.data.by_country.clicks || [];
                          const allCountries = Array.from(new Set([
                            ...viewsArr.map((v) => v.country),
                            ...clicksArr.map((c) => c.country),
                          ]));
                          const viewsMap = Object.fromEntries(viewsArr.map((v) => [v.country, v.total]));
                          const clicksMap = Object.fromEntries(clicksArr.map((c) => [c.country, c.total]));
                          return (
                            <AreaChart
                              labels={allCountries}
                              datasets={[
                                {
                                  label: t('Views'),
                                  data: allCountries.map((c) => viewsMap[c] ?? 0),
                                  backgroundColor: 'rgba(57, 106, 84, 0.18)',
                                  borderColor: 'rgba(57, 106, 84, 1)',
                                },
                                {
                                  label: t('Clicks'),
                                  data: allCountries.map((c) => clicksMap[c] ?? 0),
                                  backgroundColor: 'rgba(100, 116, 139, 0.18)',
                                  borderColor: 'rgba(100, 116, 139, 0.85)',
                                },
                              ]}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: true, position: 'top' as const },
                                },
                                scales: {
                                  y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                                  x: { grid: { display: false } },
                                },
                              }}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate Metrics — progress bars */}
                <div className="col-12">
                  <div className="card card-flush">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">{t('Rate Metrics')}</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">{t('CTR, bounce rate, scroll depth & conversion')}</span>
                      </h3>
                    </div>
                    <div className="card-body pt-4 pb-6">
                      <div className="row g-6">
                        {[
                          {
                            label: t('Click Through Rate'),
                            value: financialMetrics?.click_through_rate ?? '0%',
                            raw: parseFloat(financialMetrics?.click_through_rate ?? '0'),
                            max: 20,
                          },
                          {
                            label: t('Bounce Rate'),
                            value: adPerformance?.bounce_rate ?? '0%',
                            raw: parseFloat(adPerformance?.bounce_rate ?? '0'),
                            max: 100,
                            danger: true,
                          },
                          {
                            label: t('Scroll Depth'),
                            value: engagementMetrics?.scroll_depth ?? '0%',
                            raw: parseFloat(engagementMetrics?.scroll_depth ?? '0'),
                            max: 100,
                          },
                          {
                            label: t('Conversion Rate'),
                            value: conversionAndContact?.conversion_rate ?? '0%',
                            raw: parseFloat(conversionAndContact?.conversion_rate ?? '0'),
                            max: 20,
                          },
                        ].map((metric, i) => (
                          <div className="col-12 col-md-6" key={i}>
                            <div className="d-flex justify-content-between mb-2">
                              <span className="fw-semibold text-gray-700 fs-6">{metric.label}</span>
                              <span className="fw-bold text-dark fs-6">{metric.value}</span>
                            </div>
                            <div className="h-8px bg-gray-200 rounded">
                              <div
                                className={`h-8px rounded`}
                                style={{
                                  width: `${Math.min(100, (metric.raw / metric.max) * 100)}%`,
                                  backgroundColor: metric.danger ? (metric.raw > 60 ? '#f1416c' : '#396A54') : '#396A54',
                                  transition: 'width 0.6s ease',
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdDetails;
